/** Shared layout tokens for the dispatch desk — 3-column, 2-row grid on desktop. */

/** Page shell: fill the layout scroll column on desktop; no page-level scroll. */
export const DISPATCH_DESK_PAGE_SHELL_CLASS =
  "flex flex-col lg:min-h-0 lg:flex-1 lg:h-full lg:overflow-hidden";

/** Grid area below `PageHeader` (3.5rem). */
export const DISPATCH_DESK_PAGE_BODY_CLASS =
  "flex flex-col gap-3 p-4 md:px-6 md:pb-6 lg:min-h-0 lg:flex-1 lg:h-full lg:overflow-hidden";

/** Household assist search — grows with the header center column. */
export const DISPATCH_DESK_FINDER_WIDTH_CLASS = "min-w-0 w-full";

/** Mobile: title + shift chip row, then full-width finder; desktop: single `h-14` row. */
export const DISPATCH_DESK_PAGE_HEADER_CLASS = [
  "h-auto shrink-0 flex-col items-stretch gap-2 py-2",
  "md:h-14 md:flex-row md:items-center md:gap-2 md:py-0",
].join(" ");

export const DISPATCH_DESK_PAGE_HEADER_TOP_ROW_CLASS =
  "flex w-full items-center justify-between gap-2 md:contents";

/** Finder slot — second row on mobile, center column on `md:` (use with `order-2`). */
export const DISPATCH_DESK_FINDER_SLOT_CLASS = [
  "order-2 w-full min-w-0",
  "md:flex md:flex-1 md:justify-center md:px-4",
].join(" ");

/**
 * Desktop: 3×2 grid — top ~40% / bottom ~60% of viewport; columns share width equally.
 */
export const DISPATCH_DESK_GRID_CLASS = [
  "grid w-full grid-cols-1 gap-3",
  "lg:min-h-0 lg:flex-1 lg:grid-cols-3 lg:grid-rows-[minmax(0,2fr)_minmax(0,3fr)]",
  "lg:h-full lg:items-stretch lg:gap-x-4 lg:gap-y-2 lg:overflow-hidden",
].join(" ");

/** Col 3 — quick actions + on-duty roster; spans both grid rows on desktop. */
export const DISPATCH_DESK_ACTIONS_COLUMN_CLASS = [
  "flex w-full flex-col gap-2",
  "lg:min-h-0 lg:overflow-hidden lg:col-start-3 lg:row-span-2 lg:row-start-1 lg:h-full",
].join(" ");

/** Mobile: flatten actions column children into the page grid for reordering. @deprecated Use useIsMobile branch in dispatch-home-page instead. */
export const DISPATCH_DESK_ACTIONS_SLOT_CLASS = [
  "max-lg:contents",
  DISPATCH_DESK_ACTIONS_COLUMN_CLASS,
].join(" ");

/** Row 1 panels — stretch to the shared top grid row on desktop. */
export const DISPATCH_DESK_TOP_ROW_PANEL_CLASS =
  "flex flex-col lg:min-h-0 lg:h-full lg:max-h-full lg:overflow-hidden";

/** Col 2 row 1 — Managers + Supervisor stacked vertically (horizontal division). */
export const DISPATCH_DESK_SPLIT_TOP_ROW_CLASS =
  "flex min-w-0 flex-col gap-2 lg:col-start-2 lg:row-start-1 lg:min-h-0 lg:h-full lg:max-h-full lg:overflow-hidden";

/** Child panel inside DISPATCH_DESK_SPLIT_TOP_ROW_CLASS. */
export const DISPATCH_DESK_SPLIT_TOP_ROW_CHILD_CLASS =
  "min-h-0 min-w-0 flex-1 overflow-hidden";

/** Mobile-only stack below the desk grid. */
export const DISPATCH_DESK_MOBILE_STACK_CLASS = "flex flex-col gap-3 lg:hidden";

export const DISPATCH_DESK_GRID_GENERAL_INFO_CLASS =
  "lg:col-start-1 lg:row-start-1";

export const DISPATCH_DESK_GRID_EMERGENCY_CLASS =
  "lg:col-start-1 lg:row-start-2";

export const DISPATCH_DESK_GRID_RULES_CLASS = "lg:col-start-2 lg:row-start-2";

/** Mobile-only panel order (`< lg`); reset at desktop so grid placement applies. */
export const DISPATCH_DESK_MOBILE_ORDER_GENERAL = "order-1 lg:order-none";
export const DISPATCH_DESK_MOBILE_ORDER_ON_DUTY = "order-2 lg:order-none";
export const DISPATCH_DESK_MOBILE_ORDER_SUPERVISOR = "order-3 lg:order-none";
export const DISPATCH_DESK_MOBILE_ORDER_QUICK_ACTIONS = "order-4 lg:order-none";
export const DISPATCH_DESK_MOBILE_ORDER_EMERGENCY = "order-5 lg:order-none";
export const DISPATCH_DESK_MOBILE_ORDER_RULES = "order-6 lg:order-none";
export const DISPATCH_DESK_MOBILE_ORDER_MANAGERS = "order-7 lg:order-none";

/** @deprecated Inline map removed — use DISPATCH_DESK_ACTIONS_COLUMN_CLASS. */
export const DISPATCH_DESK_TOP_ROW_REFERENCE_CLASS = DISPATCH_DESK_ACTIONS_COLUMN_CLASS;

/** Row 2 panels — fill remaining viewport in each column on desktop. */
export const DISPATCH_DESK_BOTTOM_ROW_PANEL_CLASS =
  "lg:min-h-0 lg:h-full lg:max-h-full lg:overflow-hidden";

/** @deprecated Column wrappers removed — use DISPATCH_DESK_GRID_CLASS cells. */
export const DISPATCH_DESK_PROPERTY_IDENTITY_COLUMN_CLASS =
  "flex min-h-0 flex-col gap-2 lg:h-full lg:overflow-hidden";

/** @deprecated */
export const DISPATCH_DESK_PROPERTY_POLICY_COLUMN_CLASS =
  "flex min-h-0 flex-col gap-2 lg:h-full lg:overflow-hidden";

/** @deprecated */
export const DISPATCH_DESK_PROPERTY_COLUMN_CLASS = DISPATCH_DESK_PROPERTY_POLICY_COLUMN_CLASS;

/** Copy when no property is selected on cols 1–2. */
export const DISPATCH_DESK_NO_PROPERTY_MESSAGE = "Select a property to view details.";

/** Copy when the desk is locked until the dispatcher starts a personal shift. */
export const DISPATCH_DESK_START_SHIFT_MESSAGE =
  "Start your shift to select a property and log reports for a site.";

/** Panel shell — flex column; bounded height scroll on desktop and capped mobile panels. */
export const DISPATCH_DESK_PANEL_CLASS = "flex flex-col lg:min-h-0 lg:overflow-hidden";

/** Card body when the panel has a height cap (mobile max-h or desktop grid slice). */
export const DISPATCH_DESK_CAPPED_CARD_CONTENT_CLASS =
  "flex min-h-0 flex-1 flex-col overflow-hidden pt-0";

/** Scrollable body — use with capped panels (`DispatchDeskScrollRegion capped`). */
export const DISPATCH_DESK_PANEL_SCROLL_CLASS =
  "min-h-0 flex-1 overflow-y-auto overscroll-contain";

/** Scroll inside desktop viewport panels only; mobile grows with content (Managers, Supervisor on Call). */
export const DISPATCH_DESK_PANEL_SCROLL_DESKTOP_ONLY_CLASS =
  "lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain";

/**
 * Mobile: cap tall list panels (~half viewport) and scroll inside — mirrors desktop grid behavior.
 * Omit on short panels (Managers, Supervisor on Call) so they size to content.
 */
export const DISPATCH_DESK_MOBILE_TALL_PANEL_CAP_CLASS =
  "max-lg:max-h-[min(45vh,22rem)] max-lg:min-h-0 max-lg:overflow-hidden";

/** Mobile: shorter cap for General information. */
export const DISPATCH_DESK_MOBILE_COMPACT_PANEL_CAP_CLASS =
  "max-lg:max-h-[min(36vh,16rem)] max-lg:min-h-0 max-lg:overflow-hidden";

/** Full-screen coverage map dialog — tall enough for patrol context on wide viewports. */
export const DISPATCH_DESK_COVERAGE_MAP_DIALOG_HEIGHT_CLASS = "h-[min(70vh,40rem)]";
